/**
 * Upload command with rich terminal UI
 */

import {
  CLIOptions,
  Command,
  ExitCode,
  NetworkError,
  AuthenticationError,
  UsageLimitError,
  Config,
  ProgressTracker,
} from '@/types';
import { configLoader, EnvironmentDetector } from '@/config';
import { DiscoveryResult, ReportDiscoveryService } from '@/core/discovery';
import { UploadService } from '@/services/upload';
import type { ReportUploadResponse } from '@/services/api';
import { createProgressTracker } from '@/utils/progress';
import { EnvironmentType, EnvironmentUtils } from '@/utils/env';
import { VERSION } from '@/version';
import {
  printHeader,
  printSuccess,
  printError,
  colors,
  formatKeyValue,
} from '@/utils/ui';
import { setVerbose, logger } from '@/utils/logger';

/**
 * Main upload command that orchestrates the entire upload process
 */
export class UploadCommand implements Command {
  public readonly name = 'upload';
  public readonly description = 'Upload Playwright test reports to TestDino';

  async execute(options: CLIOptions): Promise<void> {
    const tracker = createProgressTracker();

    try {
      // Print header
      printHeader(VERSION);

      // Validate configuration
      tracker.start('Validating configuration...');
      const config = configLoader.createConfig(options);
      setVerbose(config.verbose);
      tracker.succeed('Configuration validated');

      // Show configuration summary
      if (config.verbose) {
        this.printConfigSummary(config, options);
      }

      // Discover report files
      tracker.start('Discovering report files...');
      const discoveryService = new ReportDiscoveryService(
        options.reportDirectory
      );
      const discoveryResult = await discoveryService.discover(options);

      const reportCount = this.countReports(discoveryResult);
      tracker.succeed(
        `Found ${reportCount} ${reportCount === 1 ? 'report type' : 'report types'}`
      );

      // Show discovered files in verbose mode
      if (config.verbose) {
        this.printDiscoveredFiles(discoveryResult);
      }

      // Perform upload
      const uploadService = new UploadService(config);
      const uploadResponse = await uploadService.uploadWithFallback(
        discoveryResult.jsonReport,
        discoveryResult.htmlReport,
        discoveryResult.traceDir
      );

      // Build result URL
      const testdinoUrl = this.buildResultUrl(uploadResponse);

      // Show warnings if any
      if (uploadResponse.warnings?.length) {
        logger.blank();
        for (const warning of uploadResponse.warnings) {
          logger.warn(warning);
        }
      }

      // Success!
      printSuccess('Upload completed successfully!', testdinoUrl);
    } catch (error) {
      this.handleError(error, tracker);
    }
  }

  private printConfigSummary(config: Config, options: CLIOptions): void {
    const envInfo = EnvironmentDetector.getEnvironmentInfo();

    console.log();
    console.log(colors.bold('Configuration'));
    console.log(formatKeyValue('Environment', String(envInfo.type)));
    console.log(
      formatKeyValue(
        'CI/CD',
        envInfo.isCI ? `Yes (${envInfo.ciProvider})` : 'No'
      )
    );
    console.log(formatKeyValue('Report', options.reportDirectory));

    const uploadFlags: string[] = [];
    if (config.uploadFullJson) uploadFlags.push('full-json');
    if (config.uploadImages) uploadFlags.push('images');
    if (config.uploadVideos) uploadFlags.push('videos');
    if (config.uploadHtml) uploadFlags.push('html');
    if (config.uploadTraces) uploadFlags.push('traces');
    if (config.uploadFiles) uploadFlags.push('files');

    console.log(
      formatKeyValue(
        'Upload',
        uploadFlags.length ? uploadFlags.join(', ') : 'report only'
      )
    );
    console.log();
  }

  private printDiscoveredFiles(discoveryResult: DiscoveryResult): void {
    console.log();
    console.log(colors.bold('Discovered Files'));
    console.log(formatKeyValue('JSON', discoveryResult.jsonReport));
    if (discoveryResult.htmlReport) {
      console.log(formatKeyValue('HTML', discoveryResult.htmlReport));
    }
    if (discoveryResult.traceDir) {
      console.log(formatKeyValue('Traces', discoveryResult.traceDir));
    }
    console.log();
  }

  private countReports(discoveryResult: DiscoveryResult): number {
    let count = 1; // JSON is always present
    if (discoveryResult.htmlReport) count++;
    if (discoveryResult.traceDir) count++;
    return count;
  }

  private buildResultUrl(
    uploadResponse: ReportUploadResponse
  ): string | undefined {
    // Use viewUrl if provided by the API
    if (uploadResponse.viewUrl) {
      return uploadResponse.viewUrl;
    }

    // Construct URL from components if available
    if (
      uploadResponse.organizationId &&
      uploadResponse.projectId &&
      uploadResponse.testRunId
    ) {
      const envType = EnvironmentUtils.detectEnvironmentType();
      const baseUrl =
        envType === EnvironmentType.PRODUCTION
          ? 'https://app.testdino.com'
          : envType === EnvironmentType.STAGING
            ? 'https://staging.testdino.com'
            : 'http://localhost:3001';

      return `${baseUrl}/${uploadResponse.organizationId}/projects/${uploadResponse.projectId}/test-runs/${uploadResponse.testRunId}`;
    }

    // No URL available
    return undefined;
  }

  private printQuotaError(error: UsageLimitError): void {
    const border = '═'.repeat(70);
    const { data } = error;
    const orgRemaining = (data.limit ?? 0) - (data.used ?? 0);
    const formattedResetDate = data.resetDate
      ? new Date(data.resetDate).toLocaleDateString('en-US', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
      : undefined;

    const isOrgExhausted = orgRemaining <= 0 && !data.projectName;

    console.error('');
    console.error(border);

    if (isOrgExhausted) {
      console.error('  ❌ TestDino Organization Execution Limit Reached');
      console.error(border);
      console.error('');
      console.error(
        '  Your organization has exhausted its monthly test case limit.'
      );
      console.error('');
      console.error('  Organization Usage:');
      console.error(
        `    Plan:             ${data.tier} (${data.limit || 'Unknown'} test cases / month)`
      );
      console.error(`    Used:             ${data.used || 'Unknown'}`);
      console.error('    Remaining:        0');
      console.error('');
      console.error('  Solutions:');
      console.error(
        '    1. Upgrade your plan to increase monthly test case limit'
      );
    } else {
      console.error('  ❌ TestDino Project Execution Limit Reached');
      console.error(border);
      console.error('');
      console.error(
        '  The test case limit allocated to this project has been exceeded.'
      );
      console.error('');
      const effectiveLimit =
        (data.projectLimit ?? 0) + (data.projectBorrowed ?? 0);
      console.error('  Project Usage:');
      if (data.projectName) {
        console.error(`    Project:          ${data.projectName}`);
      }
      if (data.projectLimit != null) {
        console.error(
          `    Limit:            ${effectiveLimit} test cases${data.projectBorrowed ? ` (${data.projectLimit} allocated + ${data.projectBorrowed} borrowed)` : ''}`
        );
      }
      if (data.projectUsed != null) {
        console.error(`    Used:             ${data.projectUsed}`);
      }
      console.error('    Status:           Project quota exhausted');
      console.error('');
      console.error('  Organization Usage:');
      console.error(
        `    Plan:             ${data.tier} (${data.limit || 'Unknown'} test cases / month)`
      );
      console.error(`    Used:             ${data.used || 'Unknown'}`);
      console.error(`    Remaining:        ${orgRemaining}`);
      if (orgRemaining > 0) {
        console.error('');
        console.error('  Note:');
        console.error(
          `  Your organization still has ${orgRemaining} test cases available,`
        );
        console.error('  but they are not allocated to this project.');
      }
      console.error('');
      console.error('  Solutions:');
      console.error('    1. Allocate more test case quota to this project in');
      console.error('       Settings → Billing & Usage → Test Limits');
      console.error(
        '    2. Enable Auto Allocation to automatically distribute'
      );
      console.error('       remaining organization quota');
    }

    if (formattedResetDate) {
      console.error('');
      console.error('  Monthly Reset:');
      console.error(`    ${formattedResetDate}`);
    }

    console.error('');
    console.error(
      '  Docs:    https://docs.testdino.com/platform/billing-and-usage/test-limits'
    );
    console.error('  Pricing: https://testdino.com/pricing');
    console.error(border);
    console.error('');
  }

  private handleError(error: unknown, tracker: ProgressTracker): never {
    tracker.fail('Upload failed');

    if (error instanceof AuthenticationError) {
      printError('Authentication Failed', error.message, [
        'Verify your API token is correct',
        'Check that your token has upload permissions',
        "Ensure you're using the right environment token",
      ]);
      process.exit(ExitCode.AUTHENTICATION_ERROR);
    }

    if (error instanceof NetworkError) {
      printError('Network Error', error.message, [
        'Check your internet connection',
        'Verify the API endpoint is accessible',
        'Try again in a few moments',
      ]);
      process.exit(ExitCode.NETWORK_ERROR);
    }

    if (error instanceof UsageLimitError) {
      this.printQuotaError(error);
      process.exit(ExitCode.USAGE_LIMIT_ERROR);
    }

    // Generic error
    const message = error instanceof Error ? error.message : 'Unknown error';
    printError('Upload Failed', message, [
      'Run with --verbose for more details',
      'Check https://docs.testdino.com for help',
    ]);

    process.exit(ExitCode.GENERAL_ERROR);
  }
}
